import * as React from 'react';
import { useQuery } from 'react-query';
import { Carrier } from 'contracts/Carrier';
import { api, applyQueryString } from 'services/api';
import { Formatter } from 'utils';

import { optionsMapper, multiSelectorOptionsMapper } from '../utils';

type LabelType = 'trade-name-and-document' | 'trade-name'
type HookOptions = {
  labelType?: LabelType;
}

const makeLabel = (carrier: Carrier, labelType?: LabelType): string => {
  const tradeName = carrier.tradeName;
  const document = Formatter.document(carrier.document, carrier.documentType);
  const numericDocumentLength = document.replace(/\D/g, '').length;

  switch (labelType) {
    case 'trade-name':
      return tradeName;
    case 'trade-name-and-document':
    default:
      return numericDocumentLength >= 8
        ? [tradeName, document].join(' - ')
        : tradeName;
  }
};

export const useCarrierOptions = (options?: HookOptions) => {
  const fetchOptions = React.useCallback(async () => {
    const url = applyQueryString('/carriers', {
      asList: true,
    });
    const { data } = await api.get<Carrier[]>(url);

    return data.map(carrier => ({
      value: carrier.id,
      label: makeLabel(carrier, options?.labelType),
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [options]);

  const optionsQuery = useQuery({
    queryKey: ['fetch-carrier-options'],
    queryFn: fetchOptions,
    initialData: [],
    refetchOnWindowFocus: false,
  });

  return {
    singleSelectOptions: optionsQuery.data?.map(optionsMapper) || [],
    multiSelectOptions: optionsQuery.data?.map(multiSelectorOptionsMapper) || [],
    loading: optionsQuery.isLoading,
  };
};